/**
 * Tipos GeoJSON estándar para geocodificadores WFS
 * 
 * Reemplaza `any` en parseFeature/parseResponse con tipos específicos.
 * Basado en RFC 7946 (GeoJSON) con extensiones WFS comunes.
 * 
 * @module types/geojson
 * @version 1.0.0 - TD-003.A
 */

/**
 * Geometría GeoJSON punto
 */
export interface GeoJSONPointGeometry {
  type: 'Point';
  coordinates: [number, number] | [number, number, number];
}

/**
 * Geometría GeoJSON multipunto
 */
export interface GeoJSONMultiPointGeometry {
  type: 'MultiPoint';
  coordinates: Array<[number, number] | [number, number, number]>;
}

/**
 * Geometría GeoJSON polígono
 */
export interface GeoJSONPolygonGeometry {
  type: 'Polygon';
  coordinates: Array<Array<[number, number]>>;
}

/**
 * Geometría GeoJSON multipolígono
 *
 * Sub-WEMB: introducida para soportar `cav_02_g03_06_Embalse` (n=611, 100 % MultiPolygon)
 * y, en general, cualquier capa WFS de áreas hidráulicas/superficiales en EPSG:25830.
 */
export interface GeoJSONMultiPolygonGeometry {
  type: 'MultiPolygon';
  coordinates: Array<Array<Array<[number, number]>>>;
}

/**
 * Geometría GeoJSON línea (polilínea)
 *
 * Sub-WHLG: introducida para soportar `ms:conducciones` (verts 2-77) y
 * `ms:colectores_saneamiento` (verts 2-48) en el WFS REDIAM de
 * Infraestructuras Hidráulicas (smoke 13-may, 100 % LineString puro).
 */
export interface GeoJSONLineStringGeometry {
  type: 'LineString';
  coordinates: Array<[number, number] | [number, number, number]>;
}

/**
 * Geometría GeoJSON multilínea
 *
 * Sub-WHLG: defensivo cross-tipología (coherencia con MultiPoint/MultiPolygon
 * del cluster sub-0a). Smoke 13-may sobre REDIAM hidráulico no la devuelve,
 * pero otras tipologías WFS (energético `MIEA:LineasElect`) sí.
 */
export interface GeoJSONMultiLineStringGeometry {
  type: 'MultiLineString';
  coordinates: Array<Array<[number, number] | [number, number, number]>>;
}

/**
 * Unión de geometrías soportadas
 */
export type GeoJSONGeometry = 
  | GeoJSONPointGeometry 
  | GeoJSONMultiPointGeometry 
  | GeoJSONPolygonGeometry
  | GeoJSONMultiPolygonGeometry
  | GeoJSONLineStringGeometry
  | GeoJSONMultiLineStringGeometry
  | { type: string; coordinates: unknown };

/**
 * Feature GeoJSON con propiedades genéricas
 * 
 * Usado como parámetro de parseFeature() en geocodificadores WFS.
 * Las propiedades son Record<string, unknown> para permitir
 * acceso tipado con verificación runtime.
 */
export interface GeoJSONFeature {
  type: 'Feature';
  geometry: GeoJSONGeometry | null;
  properties: Record<string, unknown> | null;
  id?: string | number;
}

/**
 * FeatureCollection GeoJSON
 * 
 * Estructura estándar de respuesta WFS en formato GeoJSON.
 */
export interface GeoJSONFeatureCollection {
  type: 'FeatureCollection';
  features: GeoJSONFeature[];
  crs?: {
    type: string;
    properties: Record<string, unknown>;
  };
  totalFeatures?: number;
  numberMatched?: number;
  numberReturned?: number;
}

/**
 * Respuesta WFS que puede ser GeoJSON o datos crudos
 * 
 * Usado como parámetro de parseResponse() en geocodificadores WFS.
 */
export type WFSResponse = GeoJSONFeatureCollection | string | unknown;

/**
 * Elemento OSM de respuesta Overpass
 */
export interface OverpassOSMElement {
  type: 'node' | 'way' | 'relation';
  id: number;
  lat?: number;
  lon?: number;
  center?: { lat: number; lon: number };
  tags?: Record<string, string>;
  nodes?: number[];
  members?: Array<{
    type: string;
    ref: number;
    role: string;
  }>;
}

/**
 * Respuesta de API Overpass
 */
export interface OverpassResponse {
  version: number;
  generator: string;
  elements: OverpassOSMElement[];
}

/**
 * Estación de gasolina de API MITMA
 */
export interface FuelStationAPIRecord {
  'IDEESS'?: string;
  'Rótulo'?: string;
  'Dirección'?: string;
  'Municipio'?: string;
  'Provincia'?: string;
  'C.P.'?: string;
  'Longitud'?: string;
  'Latitud'?: string;
  'Precio Gasolina 95 E5'?: string;
  'Precio Gasóleo A'?: string;
  [key: string]: string | undefined;
}

/**
 * Respuesta de API de gasolineras MITMA
 */
export interface FuelStationAPIResponse {
  Fecha?: string;
  ListaEESSPrecio?: FuelStationAPIRecord[];
  EstacionesTerrestres?: FuelStationAPIRecord[];
}

/**
 * Centro educativo de API Educación
 */
export interface EducationCenterRecord {
  codigo?: string | number;
  denominacion?: string;
  denominacion_especifica?: string;
  naturaleza?: string;
  tipo?: string;
  direccion?: string;
  cod_postal?: string;
  localidad?: string;
  municipio?: string;
  provincia?: string;
  latitud?: string | number;
  longitud?: string | number;
  coordenada_x?: string | number;
  coordenada_y?: string | number;
  [key: string]: unknown;
}

/**
 * Respuesta de API Educación Andalucía
 */
export interface EducationAPIResponse {
  centros?: EducationCenterRecord[];
  data?: EducationCenterRecord[];
  features?: GeoJSONFeature[];
  type?: string;
}

// =============================================================================
// HELPERS PARA ACCESO TIPADO A PROPIEDADES
// =============================================================================

/**
 * Extrae propiedad como string de Record<string, unknown>
 * 
 * @example
 * const name = getStringProp(props, 'NOMBRE', 'DENOMINACION');
 */
export function getStringProp(
  props: Record<string, unknown> | null | undefined,
  ...keys: string[]
): string {
  if (!props) return '';
  for (const key of keys) {
    const value = props[key];
    if (value !== undefined && value !== null && value !== '') {
      return String(value);
    }
  }
  return '';
}

/**
 * Extrae propiedad como número de Record<string, unknown>
 */
export function getNumberProp(
  props: Record<string, unknown> | null | undefined,
  ...keys: string[]
): number | undefined {
  if (!props) return undefined;
  for (const key of keys) {
    const value = props[key];
    if (value !== undefined && value !== null) {
      const num = typeof value === 'number' ? value : parseFloat(String(value));
      if (!isNaN(num)) return num;
    }
  }
  return undefined;
}

/**
 * Extrae propiedad como array de Record<string, unknown>
 */
export function getArrayProp<T = unknown>(
  props: Record<string, unknown> | null | undefined,
  key: string
): T[] {
  if (!props) return [];
  const value = props[key];
  return Array.isArray(value) ? value as T[] : [];
}
